import { ActionIcon, Button, Popover, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import {
  Film,
  Filter,
  Folder,
  Grip,
  Image as ImageIcon,
  Music,
  Search,
  Trash2,
  Video,
  type LucideIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import type { AssetSource } from "../../api/generated/model";
import { LoadAhead } from "../../ui/LoadAhead";
import { SoundWave } from "../../ui/SoundWave";
import { VideoFrame } from "../../ui/VideoFrame";
import type { BinItem, BinType } from "./bin-sources";
import { useSourceLength } from "./length";
import { sourceKey } from "./media";
import type { ClipSource, MediaKind } from "./timeline";
import classes from "./editor.module.css";

/** Adds a file: a video or a sound with its length, a still with none. */
export type AddMedia = (kind: MediaKind, source: ClipSource, sourceMs: number | null) => void;

/** Live's delete on an Uploads tile: shown on the files the member may delete. */
export interface RemoveFile {
  may: (item: BinItem) => boolean;
  ask: (item: BinItem) => void;
  /** The tile's bin shows (the pointer or the focus on it): its question's words may be read. */
  near?: () => void;
}

/** More pages to read: live's "Load More", or its next page on scroll. */
export interface MorePages {
  loading: boolean;
  load: () => void;
}

export interface BinListProps {
  /** The files the tab's lists answered for its search and filter (the server searches, #244). */
  items: readonly BinItem[];
  /** What it says with no files at all: live's "No brand images found". */
  empty: string;
  /** What it says when a search finds none: "No matches found" (Images and Videos say `empty`). */
  nothingFound: string;
  /** What the search box says before anything is typed: "Search uploads...". */
  searchLabel: string;
  /** The search as typed; the tab sends it to its lists 500 ms after the last key, as live's. */
  query: string;
  onQuery: (query: string) => void;
  /** The icon button beside the search, as live has it: the upload or the filter. */
  tool: ReactNode;
  /** Larger tiles, two to a row, each name shown on hover (live's Images and Videos). */
  wide?: boolean;
  /** Undefined once every page is read. */
  more: MorePages | undefined;
  /**
   * The panel's scroller: given, the next page is read as the list's end scrolls into it, with
   * 8 placeholder tiles while it comes and no button (live's Images and Videos, spec
   * capture-fixes); `null` until it mounts. Without it, "Load More" (live's Uploads).
   */
  scroller?: HTMLElement | null;
  /** Live's delete on each tile (Uploads only). */
  remove?: RemoveFile;
  full: ReadonlySet<MediaKind>;
  readOnly: boolean;
  onAdd: AddMedia;
}

/** Live's skeleton tiles under a page on its way (its editor's Images and Videos). */
const PLACEHOLDERS = 8;

/**
 * A tab's files under a search and its tool, then the next page while pages are left: on scroll
 * where the tab gives its scroller, else "Load More". The tab owns the search: it sends it to its
 * lists, which answer with the files it finds (#244 part 3).
 */
export function BinList(props: BinListProps) {
  const { items, empty, nothingFound, searchLabel, query, onQuery, tool } = props;
  const { wide = false, more, scroller, remove, full, readOnly, onAdd } = props;
  const scrolls = scroller !== undefined;
  const loading = more?.loading === true;
  // Read on scroll, a page may show nothing (another kind's outputs, or files held back until
  // the jobs are read as far): "none found" waits until every page is read (review of #1082).
  const none = items.length === 0 && (!scrolls || more === undefined);
  return (
    <Stack gap="sm">
      <div className={classes.searchRow}>
        <TextInput
          className={classes.search}
          classNames={{ input: classes.searchInput }}
          placeholder={searchLabel}
          aria-label={searchLabel.replace(/\.+$/, "")}
          leftSection={<Search size={16} aria-hidden />}
          value={query}
          onChange={(event) => onQuery(event.currentTarget.value)}
        />
        {tool}
      </div>
      {none ? (
        <Text size="sm" c="var(--cai-color-text-muted)" py="sm">
          {query.trim() === "" ? empty : nothingFound}
        </Text>
      ) : (
        <ul className={classes.binList} data-wide={wide} aria-busy={scrolls && loading}>
          {items.map((item) => (
            <BinEntry
              key={item.key}
              item={item}
              wide={wide}
              locked={readOnly || full.has(item.kind)}
              onAdd={onAdd}
              onRemove={remove?.may(item) === true ? () => remove.ask(item) : undefined}
              onRemoveNear={remove?.near}
            />
          ))}
          {scrolls && loading
            ? Array.from({ length: PLACEHOLDERS }, (_, at) => (
                <li key={`placeholder-${at}`} className={classes.binPlaceholder} aria-hidden />
              ))
            : null}
        </ul>
      )}
      {scrolls ? (
        more === undefined ? null : (
          <LoadAhead
            quiet
            more={{ has: true, loading, load: more.load }}
            loaded={items.length}
            root={scroller}
            label="Loading more files"
          />
        )
      ) : more === undefined ? null : (
        <Button
          variant="default"
          size="xs"
          className={classes.loadMore}
          loading={more.loading}
          onClick={more.load}
        >
          Load More
        </Button>
      )}
    </Stack>
  );
}

const LENGTH_TEXT = { reading: "Reading length…", unknown: "Length unknown" } as const;

/**
 * The icon an Uploads tile's pill starts with: the rail's for a video or a picture, and the upload
 * box's note for a sound.
 */
const KIND_ICON: Readonly<Record<MediaKind, LucideIcon>> = { video: Video, image: ImageIcon, audio: Music };

/**
 * One file to add, as live's tile: a square its picture fills (a sound's wave). In Uploads its name
 * is under the square, and a pill at the bottom right names its kind, more than live (spec
 * uploads-kind-pill.md): a video's camera and its length in whole seconds, a picture's icon alone,
 * a sound's note and its length. In Images and Videos the pill is the length alone, and the name
 * shows over the square's foot on hover. A video's or a sound's length is the server's, or read
 * from the file while the server has none (a library upload's, until the media worker probes it);
 * it is added once a length is known, and until then the pill says why it waits. A still needs none.
 */
function BinEntry(props: {
  item: BinItem;
  wide: boolean;
  locked: boolean;
  onAdd: AddMedia;
  /** Live's bin at the tile's top right, shown on hover or focus. */
  onRemove: (() => void) | undefined;
  onRemoveNear?: (() => void) | undefined;
}) {
  const { item, wide, locked, onAdd, onRemove, onRemoveNear } = props;
  const near = onRemove === undefined ? undefined : onRemoveNear;
  const { kind, source, media, prime } = item;
  const queryClient = useQueryClient();
  const timed = kind !== "image";
  const length = useSourceLength(sourceKey(source), timed ? media.url : null, media.durationMs);
  const ready = !timed || length.state === "known";
  // Whole seconds, as live's Videos tab shows them; a length still read, or unknown, is said.
  const lengthText = length.state === "known" ? `${Math.floor(length.ms / 1000)}s` : LENGTH_TEXT[length.state];
  const KindIcon = KIND_ICON[kind];
  return (
    <li className={classes.binEntry}>
      <UnstyledButton
        // The tile under the pointer or the focus shows its bin: its question's days are read.
        onPointerEnter={near}
        onFocus={near}
        className={classes.binItem}
        data-wide={wide}
        aria-label={`Add ${media.name} to the timeline`}
        disabled={!ready || locked}
        onClick={() => {
          if (!ready) return;
          prime(queryClient);
          onAdd(kind, source, length.state === "known" ? length.ms : null);
        }}
      >
        <span className={classes.binThumb} data-kind={kind}>
          {kind === "audio" ? (
            <SoundWave className={classes.binWave} />
          ) : media.poster !== null ? (
            <img src={media.poster} alt="" className={classes.binPoster} />
          ) : media.url !== null && kind === "video" ? (
            // No poster yet (S2 makes them): a frame of the video itself.
            <VideoFrame src={media.url} fallback={<Film size={20} aria-hidden />} />
          ) : (
            <Film size={20} aria-hidden />
          )}
          {wide ? (
            timed ? <span className={classes.binLength}>{lengthText}</span> : null
          ) : (
            <span className={classes.binKind} data-icon-only={!timed || undefined}>
              <KindIcon size={9} strokeWidth={2.4} aria-hidden />
              {timed ? lengthText : null}
            </span>
          )}
          {/* Its 540p copy still being made (spec proxy-playback.md): the preview plays the original. */}
          {kind === "video" && media.proxyStatus === "pending" ? (
            <span className={classes.binNote}>Preparing preview</span>
          ) : null}
          {wide ? <span className={classes.binHoverName}>{media.name}</span> : null}
        </span>
        {wide ? null : <span className={classes.binName}>{media.name}</span>}
      </UnstyledButton>
      {onRemove === undefined ? null : (
        <ActionIcon
          variant="subtle"
          size={20}
          className={classes.binRemove}
          aria-label={`Delete ${media.name}`}
          onPointerEnter={near}
          onFocus={near}
          onClick={onRemove}
        >
          <Trash2 size={12} aria-hidden />
        </ActionIcon>
      )}
    </li>
  );
}

/** Live's filter: the file's type; "all" lists every file but a product's photo and a logo. */
export type TypeFilter = "all" | Exclude<BinType, "other">;
/** The types the library lists: Avatar is the team's actors, not a library file. */
export type FileType = Exclude<TypeFilter, "avatar">;

/** Live's filter's second group: every file, or the member's current folder's own. */
export type ProjectFilter = "all" | "current";

export interface BinFilter {
  type: TypeFilter;
  project: ProjectFilter;
}

const TYPES: readonly { value: TypeFilter; label: string }[] = [
  { value: "all", label: "All Types" },
  { value: "creative", label: "Creative" },
  { value: "manual", label: "Manual" },
  { value: "product", label: "Product" },
  { value: "avatar", label: "Avatar" },
];

/**
 * The library's sources of a type (the list route's `source`, any of them). All Types is live's
 * nine types, none of them a product's photo or an avatar (spec editor/media-panel.md decision 3);
 * Manual is an upload or a URL import.
 */
export const SOURCES_OF: Record<FileType, readonly AssetSource[]> = {
  all: ["upload", "url_import", "generation"],
  creative: ["generation"],
  manual: ["upload", "url_import"],
  product: ["product_image"],
};

/**
 * Live's filter beside the Images and Videos searches: a popover of two groups, the file's type
 * (All Types, Creative, Manual, Product, Avatar) and its project (All Projects, Current Project:
 * the folder the member used last, spec editor/media-panel.md), each row with a dot that fills
 * when chosen; a choice closes it. Avatar lists the team's actors.
 */
export function TypeFilterButton(props: { value: BinFilter; onChange: (filter: BinFilter) => void }) {
  const { value, onChange } = props;
  const [opened, setOpened] = useState(false);
  const row = (label: string, chosen: boolean, onClick: () => void) => (
    <UnstyledButton
      key={label}
      className={classes.filterRow}
      aria-pressed={chosen}
      onClick={onClick}
    >
      {label}
      <span className={classes.filterDot} data-chosen={chosen} aria-hidden />
    </UnstyledButton>
  );
  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" width={208} shadow="xl">
      <Popover.Target>
        <ActionIcon
          variant="default"
          size={36}
          className={classes.searchTool}
          aria-label="Filter"
          aria-expanded={opened}
          aria-haspopup="dialog"
          onClick={() => setOpened(!opened)}
        >
          <Filter size={16} aria-hidden />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown className={classes.filterPanel} aria-label="Filter">
        <fieldset className={classes.filterSet}>
          <legend className={classes.filterGroup}>
            <Grip size={13} aria-hidden />
            All Types
          </legend>
          {TYPES.map((type) =>
            row(type.label, value.type === type.value, () => {
              onChange({ ...value, type: type.value });
              setOpened(false);
            }),
          )}
        </fieldset>
        <div className={classes.filterRule} />
        <fieldset className={classes.filterSet}>
          <legend className={classes.filterGroup}>
            <Folder size={13} aria-hidden />
            All Assets
          </legend>
          {row("All Projects", value.project === "all", () => {
            onChange({ ...value, project: "all" });
            setOpened(false);
          })}
          {row("Current Project", value.project === "current", () => {
            onChange({ ...value, project: "current" });
            setOpened(false);
          })}
        </fieldset>
      </Popover.Dropdown>
    </Popover>
  );
}
